// =============================================================================
// src/features/explore/explore-geo.ts — Geometria pura (centroide de polígono).
//
// Motivo: a bolha de voto era posicionada no centro da BOUNDING BOX do município
//   (bounds.getCenter()), o que p/ formas irregulares/ribeirinhas do AM caía perto
//   da borda. Aqui calculamos o centroide REAL por área assinada do maior anel do
//   polígono, p/ a bolha sentar no centro visual da forma (pedido do usuário).
//
// SRP: só matemática de geometria. Sem DOM, sem Leaflet, sem dataset. Entrada =
//   GeoJSON.Geometry ([lng,lat]); saída = [lat,lng] (ordem do Leaflet) ou null.
// =============================================================================

/** Anel de coordenadas GeoJSON [lng,lat] (os pares sao bem-formados nos arquivos). */
type Ring = readonly (readonly number[])[];

/** Centroide por área assinada de um anel; null se < 3 pontos ou área ~0. */
function ringCentroid(ring: Ring): { lat: number; lng: number; area: number } | null {
  const n = ring.length;
  if (n < 3) return null;
  let acc = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < n; i += 1) {
    // In-bounds por construcao (i e (i+1)%n) — GeoJSON traz pares [lng,lat] completos.
    const p = ring[i] as readonly number[];
    const q = ring[(i + 1) % n] as readonly number[];
    const cross = (p[0] as number) * (q[1] as number) - (q[0] as number) * (p[1] as number);
    acc += cross;
    cx += ((p[0] as number) + (q[0] as number)) * cross;
    cy += ((p[1] as number) + (q[1] as number)) * cross;
  }
  if (Math.abs(acc) < 1e-12) return null;
  const area = acc / 2;
  return { lng: cx / (6 * area), lat: cy / (6 * area), area: Math.abs(area) };
}

/** Maior anel externo de um Polygon/MultiPolygon (por área); null p/ outras geometrias. */
function largestOuterRing(geometry: GeoJSON.Geometry): Ring | null {
  if (geometry.type === 'Polygon') return (geometry.coordinates[0] as Ring | undefined) ?? null;
  if (geometry.type !== 'MultiPolygon') return null;
  let best: Ring | null = null;
  let bestArea = -1;
  for (const polygon of geometry.coordinates) {
    const ring = polygon[0] as Ring | undefined;
    const centroid = ring ? ringCentroid(ring) : null;
    if (ring && centroid && centroid.area > bestArea) { bestArea = centroid.area; best = ring; }
  }
  return best;
}

/** Centroide real [lat,lng] do município (maior anel); null se geometria degenerada. */
export function polygonCentroidOf(geometry: GeoJSON.Geometry): readonly [number, number] | null {
  const ring = largestOuterRing(geometry);
  const centroid = ring ? ringCentroid(ring) : null;
  return centroid ? [centroid.lat, centroid.lng] : null;
}
